/**
 * Telas de acesso (spec 001, secao 5 e apendice A). So apresentacao: recebem estado e devolvem acoes.
 * Os apps ligam na API; o Storybook mostra os estados. Mesmas telas nas duas marcas.
 */
import { useState, type FormEvent } from "react";
import { Aviso, Etapas, ListaDeAjuda, Segmentado, Separador } from "../componentes/Basicos";
import { Botao } from "../componentes/Botao";
import { Campo, CampoCodigo, CampoCpf, CampoDinheiro, CampoSenha } from "../componentes/Campo";
import { FormularioEntrar, FormularioEsqueciSenha, ROTAS_SUPORTE, SENHA_MIN } from "../componentes/FormulariosDeAcesso";
import { TelaDeAcesso, type Marca } from "../componentes/TelaDeAcesso";
import { cpfValido, emailValido, soDigitos } from "../formatos";
import { IconeEmail, IconeMensagem, IconeVoltar } from "../icones";
import { LinkInterno } from "../navegacao";

const AREA = "Área do cliente";
const PAINEL = "Painel de operação";
const FRASE_AREA = "Seu plano, sua conta e seus resultados em um só lugar.";

type Comum = { marca: Marca; erro?: string; carregando?: boolean };

function Cabeca({ titulo, subtitulo }: { titulo: string; subtitulo?: string }) {
  return (
    <header className="mtc-acesso__cabeca">
      <h1 className="mtc-titulo mtc-titulo--1">{titulo}</h1>
      {subtitulo && <p className="mtc-acesso__subtitulo">{subtitulo}</p>}
    </header>
  );
}

function Voltar({ href = "/entrar", texto = "Voltar para o login" }: { href?: string; texto?: string }) {
  return <LinkInterno href={href} className="mtc-voltar"><IconeVoltar tamanho={16} />{texto}</LinkInterno>;
}

// ---------------------------------------------------------------- area do trader: entrar

export function TelaEntrarTrader({ marca, erro, carregando, aviso, aoEnviar }: Comum & { aviso?: string; aoEnviar: (d: { identificador: string; senha: string }) => void }) {
  return (
    <TelaDeAcesso marca={marca} rotulo={AREA} frase={FRASE_AREA} rodape={marca.linkPlanos ? <>Ainda não tem um plano? <a href={marca.linkPlanos}>Conheça os planos</a></> : undefined}>
      <Cabeca titulo="Entrar" subtitulo="Use o CPF ou o e-mail da sua compra." />
      <FormularioEntrar aviso={aviso} erro={erro} carregando={carregando} aoEnviar={aoEnviar} />
      <Separador>Precisa de ajuda?</Separador>
      <ListaDeAjuda
        itens={[
          { titulo: "Esqueci minha senha", descricao: "Receba um código no seu e-mail e crie outra.", href: ROTAS_SUPORTE.senha },
          { titulo: "Cadastrei o e-mail errado ou não tenho acesso a ele", descricao: "Troque o e-mail provando o pagamento.", href: ROTAS_SUPORTE.email },
          { titulo: "Comprei com outro CPF ou o CPF está errado", descricao: "Resolvemos com você pelo suporte.", href: ROTAS_SUPORTE.cpf },
        ]}
      />
    </TelaDeAcesso>
  );
}

// ---------------------------------------------------------------- ja tem alguem conectado neste aparelho

/**
 * Quem chega por uma compra ou por um link de acesso e encontra outra sessao aberta no aparelho (celular emprestado,
 * computador da familia) nao pode cair na conta de outra pessoa. A tela pergunta: continuar com a conta aberta ou sair.
 * `motivo`: "compra" (voltou do checkout) ou "link" (o link de criar senha do e-mail, que so vale saindo).
 */
export function TelaContaConectada({ marca, nome, motivo, carregando, aoContinuar, aoTrocar }: { marca: Marca; nome: string; motivo: "compra" | "link"; carregando?: boolean; aoContinuar: () => void; aoTrocar: () => void }) {
  const primeiro = nome.trim().split(/\s+/)[0] ?? nome;
  const trocar = <Botao largo variante={motivo === "link" ? "primario" : "secundario"} carregando={carregando} onClick={aoTrocar}>{motivo === "link" ? "Sair e criar a senha" : "Sair e entrar com outra conta"}</Botao>;
  const continuar = <Botao largo variante={motivo === "link" ? "secundario" : "primario"} disabled={carregando} onClick={aoContinuar}>Continuar como {primeiro}</Botao>;
  return (
    <TelaDeAcesso marca={marca} rotulo={AREA} frase={FRASE_AREA}>
      <Cabeca
        titulo="Você já está conectado"
        subtitulo={motivo === "link"
          ? `Este aparelho está com a conta de ${nome}. O link que você abriu pode ser de outra conta: pra criar a senha, saia primeiro.`
          : `Este aparelho está com a conta de ${nome}. Se a compra foi em outro CPF, saia e entre com a conta dela.`}
      />
      <div className="mtc-acesso__campos">
        {motivo === "link" ? <>{trocar}{continuar}</> : <>{continuar}{trocar}</>}
      </div>
    </TelaDeAcesso>
  );
}

// ---------------------------------------------------------------- esqueci minha senha (trader e operador)

export type PropsEsqueciSenha = Comum & {
  publico: "area" | "painel";
  etapa: "identificar" | "codigo";
  emailMascarado?: string;
  aoPedirCodigo: (identificador: string) => void;
  aoConfirmar: (d: { codigo: string; novaSenha: string }) => void;
  aoTrocarDado?: () => void;
};

export function TelaEsqueciSenha({ marca, erro, carregando, publico, etapa, emailMascarado, aoPedirCodigo, aoConfirmar, aoTrocarDado }: PropsEsqueciSenha) {
  const area = publico === "area";
  return (
    <TelaDeAcesso marca={marca} rotulo={area ? AREA : PAINEL} frase={area ? FRASE_AREA : undefined}>
      <Voltar />
      <Cabeca titulo="Esqueci minha senha" subtitulo={etapa === "identificar" ? (area ? "Informe seu CPF ou o e-mail de acesso. Vamos enviar um código de 6 dígitos para o e-mail principal cadastrado." : "Informe o seu e-mail de acesso. Vamos enviar um código de 6 dígitos para ele.") : undefined} />
      <Etapas etapas={["Seus dados", "Código e nova senha"]} atual={etapa === "identificar" ? 0 : 1} />
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      <FormularioEsqueciSenha publico={publico} etapa={etapa} emailMascarado={emailMascarado} carregando={carregando} aoPedirCodigo={aoPedirCodigo} aoConfirmar={aoConfirmar} aoTrocarDado={aoTrocarDado} />
    </TelaDeAcesso>
  );
}

// ---------------------------------------------------------------- cadastrei o e-mail errado (trader)

export type Prova = { tipo: "pix"; e2e: string } | { tipo: "cartao"; final: string; valor_centavos: number };

export type PropsEmailErrado = Comum & {
  etapa: "dados" | "codigo";
  emailMascaradoNovo?: string;
  aoEnviarDados: (d: { cpf: string; prova: Prova; emailNovo: string }) => void;
  aoConfirmar: (d: { codigo: string; novaSenha: string }) => void;
  aoRecomecar?: () => void;
};

export function TelaEmailErrado({ marca, erro, carregando, etapa, emailMascaradoNovo, aoEnviarDados, aoConfirmar, aoRecomecar }: PropsEmailErrado) {
  const [cpf, setCpf] = useState("");
  const [metodo, setMetodo] = useState<"pix" | "cartao">("pix");
  const [e2e, setE2e] = useState("");
  const [finalCartao, setFinalCartao] = useState("");
  const [centavos, setCentavos] = useState(0);
  const [emailNovo, setEmailNovo] = useState("");
  const [codigo, setCodigo] = useState("");
  const [novaSenha, setNovaSenha] = useState("");
  const [tentou, setTentou] = useState(false);

  const erroCpf = tentou && !cpfValido(cpf) ? "Confira o CPF." : undefined;
  const erroE2e = tentou && metodo === "pix" && e2e.trim().length < 20 ? "O ID da transação tem uns 32 caracteres e começa com E." : undefined;
  const erroFinal = tentou && metodo === "cartao" && soDigitos(finalCartao).length !== 4 ? "Informe os 4 últimos dígitos." : undefined;
  const erroValor = tentou && metodo === "cartao" && centavos <= 0 ? "Informe o valor pago." : undefined;
  const erroEmail = tentou && !emailValido(emailNovo) ? "Confira o e-mail." : undefined;

  const enviarDados = (e: FormEvent) => {
    e.preventDefault();
    setTentou(true);
    const ok = cpfValido(cpf) && emailValido(emailNovo) && (metodo === "pix" ? e2e.trim().length >= 20 : soDigitos(finalCartao).length === 4 && centavos > 0);
    if (!ok) return;
    aoEnviarDados({ cpf: soDigitos(cpf), emailNovo: emailNovo.trim(), prova: metodo === "pix" ? { tipo: "pix", e2e: e2e.trim() } : { tipo: "cartao", final: soDigitos(finalCartao), valor_centavos: centavos } });
  };

  return (
    <TelaDeAcesso marca={marca} rotulo={AREA} frase={FRASE_AREA}>
      <Voltar />
      <Cabeca titulo="E-mail errado" subtitulo={etapa === "dados" ? `Você comprou um plano na ${marca.nome}, mas o e-mail da compra está errado ou você não tem mais acesso a ele. Prove que a compra foi sua e troque o e-mail.` : undefined} />
      <Etapas etapas={["CPF e pagamento", "Código e nova senha"]} atual={etapa === "dados" ? 0 : 1} />
      {erro && <Aviso tipo="erro">{erro}</Aviso>}
      {etapa === "dados" ? (
        <form className="mtc-acesso__campos" onSubmit={enviarDados} noValidate>
          <CampoCpf rotulo="CPF da compra" valor={cpf} aoMudar={setCpf} erro={erroCpf} autoFocus />
          <Segmentado rotulo="Como você pagou?" nome="metodo" valor={metodo} aoMudar={setMetodo} opcoes={[{ valor: "pix", rotulo: "PIX" }, { valor: "cartao", rotulo: "Cartão de crédito" }]} />
          {metodo === "pix" ? (
            <Campo rotulo="ID da transação do PIX" valor={e2e} aoMudar={(v) => setE2e(v.replace(/\s/g, "").toUpperCase())} mono placeholder="E00000000202609..." erro={erroE2e} dica="No comprovante do seu banco. É um código longo que começa com E." autoComplete="off" spellCheck={false} />
          ) : (
            <div style={{ display: "grid", gap: "var(--e-4)", gridTemplateColumns: "minmax(0,1fr) minmax(0,1.3fr)" }}>
              <Campo rotulo="Final do cartão" valor={finalCartao} aoMudar={(v) => setFinalCartao(soDigitos(v).slice(0, 4))} mono inputMode="numeric" placeholder="0000" erro={erroFinal} />
              <CampoDinheiro rotulo="Valor pago" centavos={centavos} aoMudar={setCentavos} erro={erroValor} />
            </div>
          )}
          <Campo rotulo="E-mail correto" type="email" valor={emailNovo} aoMudar={setEmailNovo} autoComplete="email" autoCapitalize="none" spellCheck={false} erro={erroEmail} dica="O código vai para este e-mail." />
          <Botao type="submit" largo carregando={carregando}>Enviar código</Botao>
          <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>Seu CPF não muda neste processo. Comprou com o CPF de outra pessoa? <LinkInterno href={ROTAS_SUPORTE.cpf}>Veja como resolver</LinkInterno>.</p>
        </form>
      ) : (
        <form className="mtc-acesso__campos" onSubmit={(e) => { e.preventDefault(); if (codigo.length === 6 && novaSenha.length >= SENHA_MIN) aoConfirmar({ codigo, novaSenha }); }} noValidate>
          <Aviso tipo="info">Enviamos um código para <strong className="mtc-mono">{emailMascaradoNovo ?? "o e-mail informado"}</strong>. Ele vale por 10 minutos.</Aviso>
          <CampoCodigo valor={codigo} aoMudar={setCodigo} autoFocus />
          <CampoSenha rotulo="Nova senha" valor={novaSenha} aoMudar={setNovaSenha} autoComplete="new-password" dica={`Mínimo de ${SENHA_MIN} caracteres.`} />
          <Botao type="submit" largo carregando={carregando} disabled={codigo.length !== 6 || novaSenha.length < SENHA_MIN}>Confirmar e entrar</Botao>
          {aoRecomecar && <button type="button" className="mtc-botao mtc-botao--fantasma mtc-botao--pequeno" onClick={aoRecomecar}>Corrigir os dados</button>}
        </form>
      )}
    </TelaDeAcesso>
  );
}

// ---------------------------------------------------------------- CPF errado (trader)

export function TelaCpfErrado({ marca }: { marca: Marca }) {
  const whats = marca.whatsapp;
  return (
    <TelaDeAcesso marca={marca} rotulo={AREA} frase={FRASE_AREA}>
      <Voltar />
      <Cabeca titulo="CPF errado" subtitulo={`O CPF é a identidade da sua conta na ${marca.nome} e não pode ser alterado pela área do trader. Isso protege você: ninguém troca o dono de um plano sem passar por uma pessoa da nossa equipe.`} />
      <div className="mtc-cartao" style={{ display: "grid", gap: "var(--e-3)" }}>
        <span className="mtc-rotulo">Tenha em mãos</span>
        <ul className="mtc-lista-simples">
          <li>um documento com foto do titular correto;</li>
          <li>o comprovante do pagamento, PIX ou cartão;</li>
          <li>o e-mail e o CPF usados na compra.</li>
        </ul>
      </div>
      <div style={{ display: "grid", gap: "var(--e-3)" }}>
        {whats && <a className="mtc-botao mtc-botao--primario mtc-botao--largo" href={whats} target="_blank" rel="noreferrer"><IconeMensagem tamanho={18} />Falar com o suporte no WhatsApp</a>}
        {marca.suporteEmail && <a className={`mtc-botao ${whats ? "mtc-botao--secundario" : "mtc-botao--primario"} mtc-botao--largo`} href={`mailto:${marca.suporteEmail}?subject=${encodeURIComponent("Correção de CPF")}`}><IconeEmail tamanho={18} />Escrever para {marca.suporteEmail}</a>}
      </div>
      <p className="mtc-texto-2" style={{ margin: 0, fontSize: "var(--t-sm)" }}>A equipe confere os dados e faz a correção. Como a conta na corretora também é vinculada ao CPF, isso pode levar até um dia útil.</p>
    </TelaDeAcesso>
  );
}

// ---------------------------------------------------------------- definir senha (link do e-mail)

export function TelaDefinirSenha({ marca, erro, carregando, estado, aoDefinir }: Comum & { estado: "formulario" | "invalido"; aoDefinir: (senha: string) => void }) {
  const [senha, setSenha] = useState("");
  return (
    <TelaDeAcesso marca={marca} rotulo={AREA} frase={FRASE_AREA}>
      {estado === "invalido" ? (
        <>
          <Cabeca titulo="Link vencido" subtitulo="Este link é inválido, expirou ou já foi usado. Os links de acesso valem por 7 dias e funcionam uma vez só." />
          <LinkInterno href={ROTAS_SUPORTE.senha} className="mtc-botao mtc-botao--primario mtc-botao--largo">Receber um código por e-mail</LinkInterno>
          <Voltar />
        </>
      ) : (
        <>
          <Cabeca titulo="Crie sua senha" subtitulo={`Sua conta na ${marca.nome} está pronta. Falta só a senha para entrar.`} />
          <form className="mtc-acesso__campos" onSubmit={(e) => { e.preventDefault(); if (senha.length >= SENHA_MIN) aoDefinir(senha); }} noValidate>
            {erro && <Aviso tipo="erro">{erro}</Aviso>}
            <CampoSenha rotulo="Nova senha" valor={senha} aoMudar={setSenha} autoComplete="new-password" autoFocus dica={`Mínimo de ${SENHA_MIN} caracteres.`} />
            <Botao type="submit" largo carregando={carregando} disabled={senha.length < SENHA_MIN}>Criar senha e entrar</Botao>
          </form>
        </>
      )}
    </TelaDeAcesso>
  );
}

// ---------------------------------------------------------------- painel: entrar

export function TelaEntrarPainel({ marca, erro, carregando, aviso, aoEnviar }: Comum & { aviso?: string; aoEnviar: (d: { email: string; senha: string }) => void }) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  return (
    <TelaDeAcesso marca={marca} rotulo={PAINEL} frase={`Acesso restrito à equipe da ${marca.nome}.`}>
      <Cabeca titulo="Entrar" subtitulo="Use o e-mail e a senha de operador." />
      <form className="mtc-acesso__campos" onSubmit={(e) => { e.preventDefault(); if (email.trim() && senha) aoEnviar({ email: email.trim(), senha }); }} noValidate>
        {aviso && <Aviso tipo="sucesso">{aviso}</Aviso>}
        {erro && <Aviso tipo="erro">{erro}</Aviso>}
        <Campo rotulo="E-mail" type="email" valor={email} aoMudar={setEmail} autoComplete="username" autoCapitalize="none" spellCheck={false} autoFocus />
        <CampoSenha valor={senha} aoMudar={setSenha} />
        <Botao type="submit" largo carregando={carregando} disabled={!email.trim() || !senha}>Entrar</Botao>
      </form>
      <div className="mtc-acesso__linha"><LinkInterno href="/esqueci-senha">Esqueci minha senha</LinkInterno></div>
    </TelaDeAcesso>
  );
}
